<template>
  <component
    :is="embedded ? 'div' : 'basic-page'"
    :title="embedded ? undefined : 'Talous & Budjetti'"
    :class="{ 'budjetti-embedded': embedded }"
  >
    <!-- Access check for non-hallitus users -->
    <v-alert v-if="!canView" type="error" outlined class="ma-4">
      Sinulla ei ole oikeuksia tarkastella budjettia. Budjetin katselu on sallittu vain hallituksen jäsenille.
    </v-alert>

    <div v-else>
      <!-- Top Control Bar -->
    <v-card class="mb-4">
      <v-card-text class="py-3">
        <v-row dense align="center" justify="space-between">
          <v-col cols="12" sm="4" md="3" class="d-flex align-center">
            <v-icon left color="primary">mdi-calendar-range</v-icon>
            <v-select
              v-model="selectedYear"
              :items="availableYears"
              label="Valitse vuosi"
              outlined
              dense
              hide-details
              class="font-weight-bold"
              style="max-width: 150px"
              @change="onYearChange"
            ></v-select>
            <v-btn
              v-if="canManage"
              icon
              small
              color="primary"
              class="ml-2"
              title="Luo uusi vuosi tai tuo budjettipohja"
              @click="showCreateDialog = true"
            >
              <v-icon>mdi-plus</v-icon>
            </v-btn>
          </v-col>

          <v-col
            cols="12"
            sm="8"
            md="9"
            class="d-flex justify-end align-center flex-wrap"
          >
            <v-btn
              v-if="budgetData"
              outlined
              color="deep-purple"
              class="mr-2 mb-1"
              @click="showHistoryDialog = true"
              title="Näytä tallennushistoria, muutosloki ja palaa aiempiin versioihin"
            >
              <v-icon left>mdi-history</v-icon>
              Muutosloki & Historia
            </v-btn>

            <v-btn
              v-if="canManage && budgetData"
              color="primary"
              class="mr-2 mb-1"
              @click="showCsvImportDialog = true"
            >
              <v-icon left>mdi-upload</v-icon>
              Tuo KP-raportti (.csv)
            </v-btn>

            <!-- MUOKKAUSTILA TOGGLE -->
            <v-btn
              v-if="canManage && budgetData"
              :color="isEditMode ? 'amber darken-3' : 'grey darken-1'"
              :outlined="!isEditMode"
              :dark="isEditMode"
              class="mr-2 mb-1 font-weight-bold"
              @click="isEditMode = !isEditMode"
              :title="isEditMode ? 'Poistu muokkaustilasta' : 'Avaa muokkaustila (näytä rivien siirto- ja muokkaustoiminnot)'"
            >
              <v-icon left>{{ isEditMode ? 'mdi-pencil' : 'mdi-pencil-outline' }}</v-icon>
              {{ isEditMode ? 'Muokkaustila PÄÄLLÄ' : 'Muokkaustila' }}
            </v-btn>

            <v-btn
              v-if="canManage && budgetData && currentProject && isEditMode"
              outlined
              color="primary"
              class="mr-2 mb-1"
              @click="openAddAccount"
            >
              <v-icon left>mdi-plus</v-icon>
              Lisää tili
            </v-btn>

            <v-btn
              v-if="canManage && budgetData && currentProject && isEditMode"
              outlined
              color="primary"
              class="mr-2 mb-1"
              @click="openAddTitle"
            >
              <v-icon left>mdi-format-header-pound</v-icon>
              Lisää otsikkorivi
            </v-btn>

            <v-menu offset-y v-if="canManage">
              <template v-slot:activator="{ on, attrs }">
                <v-btn outlined v-bind="attrs" v-on="on" class="mr-2 mb-1">
                  <v-icon left>mdi-cog-outline</v-icon>
                  Asetukset
                </v-btn>
              </template>
              <v-list dense>
                <v-list-item @click="showCreateDialog = true">
                  <v-list-item-icon
                    ><v-icon small>mdi-file-excel</v-icon></v-list-item-icon
                  >
                  <v-list-item-title
                    >Päivitä / Tuo Excel (.xlsx)</v-list-item-title
                  >
                </v-list-item>
                <v-list-item @click="openAddAccount">
                  <v-list-item-icon
                    ><v-icon small>mdi-plus-circle</v-icon></v-list-item-icon
                  >
                  <v-list-item-title>Lisää budjettitili</v-list-item-title>
                </v-list-item>
                <v-list-item @click="openAddTitle">
                  <v-list-item-icon
                    ><v-icon small>mdi-format-header-pound</v-icon></v-list-item-icon
                  >
                  <v-list-item-title>Lisää otsikkorivi</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>

            <v-btn
              outlined
              class="mb-1"
              :disabled="!budgetData"
              @click="exportToExcel"
              title="Lataa nykyinen budjettitilanne Excel-muodossa"
            >
              <v-icon left>mdi-download</v-icon>
              Vie Exceliin
            </v-btn>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- Empty state when no budget exists for the year -->
    <v-card v-if="!loading && !budgetData" class="pa-8 text-center my-6">
      <v-icon size="64" color="grey lighten-1">mdi-calculator</v-icon>
      <div class="text-h5 font-weight-bold mt-4 mb-2">
        Vuodelle {{ selectedYear }} ei ole vielä luotu budjettia
      </div>
      <div
        class="text-body-1 grey--text text--darken-1 mb-6"
        style="max-width: 600px; margin: 0 auto"
      >
        Voit alustaa budjetin vuoden 2026 valmiista talousarviosta (1-Budjetti
        2026.xlsx), ladata uuden Excel-pohjan tai luoda puhtaan budjetin
        tyhjästä.
      </div>
      <div class="d-flex justify-center flex-wrap" v-if="canManage">
        <v-btn
          v-if="selectedYear === 2026"
          color="primary"
          large
          class="mr-3 mb-2"
          @click="seed2026Budget"
          :loading="seeding"
        >
          <v-icon left>mdi-file-excel</v-icon>
          Alusta vuoden 2026 budjetti (1-Budjetti 2026.xlsx)
        </v-btn>
        <v-btn
          outlined
          color="primary"
          large
          class="mb-2"
          @click="showCreateDialog = true"
        >
          <v-icon left>mdi-plus</v-icon>
          Luo tyhjä tai tuo muu pohja
        </v-btn>
      </div>
    </v-card>

    <!-- Budget Content -->
    <div v-if="budgetData">
      <!-- Cost Center Tabs -->
      <v-card class="mb-4">
        <v-tabs
          v-model="activeTab"
          show-arrows
          background-color="transparent"
          color="primary"
        >
          <v-tab href="#overview">
            <v-icon left small>mdi-chart-pie</v-icon>
            Yhteenveto
          </v-tab>
          <v-tab href="#transactions">
            <v-icon left small>mdi-receipt-text-outline</v-icon>
            KP-tapahtumat
            <v-chip
              v-if="transactions.length > 0"
              x-small
              class="ml-2 font-weight-bold"
              color="primary"
              text-color="white"
            >
              {{ transactions.length }}
            </v-chip>
          </v-tab>
          <v-tab
            v-for="proj in budgetData.projects"
            :key="proj.id"
            :href="`#proj-${proj.id}`"
          >
            <strong>{{ proj.code }}</strong>&nbsp;{{ proj.name }}
          </v-tab>
        </v-tabs>
      </v-card>

      <!-- KPI Summary Cards: Tuotot, Kulut, Tulos, Kulutusaste -->
      <v-row dense class="mb-4" v-if="activeTab !== 'transactions'">
        <!-- 1. TUOTOT -->
        <v-col cols="12" sm="6" md="3">
          <v-card outlined class="pa-4 text-center border-left-green">
            <div
              class="text-caption green--text text--darken-3 text-uppercase font-weight-bold d-flex align-center justify-center"
            >
              <v-icon left x-small color="green darken-2">mdi-cash-plus</v-icon>
              Tuotot (Tulot)
            </div>
            <div
              class="text-h5 font-weight-black mt-1 green--text text--darken-2"
            >
              {{ formatCurrency(currentKPIs.incomeToteutuma) }}
            </div>
            <div class="text-caption grey--text mt-1">
              Arvio: {{ formatCurrency(currentKPIs.incomeArvio) }}
              <span
                class="ml-1"
                :class="
                  currentKPIs.incomeErotus <= 0
                    ? 'green--text font-weight-bold'
                    : 'orange--text font-weight-bold'
                "
              >
                ({{ formatDiff(currentKPIs.incomeErotus) }})
              </span>
            </div>
          </v-card>
        </v-col>

        <!-- 2. KULUT -->
        <v-col cols="12" sm="6" md="3">
          <v-card outlined class="pa-4 text-center border-left-red">
            <div
              class="text-caption red--text text--darken-3 text-uppercase font-weight-bold d-flex align-center justify-center"
            >
              <v-icon left x-small color="red darken-2">mdi-cash-minus</v-icon>
              Kulut (Menot)
            </div>
            <div class="text-h5 font-weight-black mt-1 red--text text--darken-2">
              {{ formatCurrency(currentKPIs.expenseToteutuma) }}
            </div>
            <div class="text-caption grey--text mt-1">
              Arvio: {{ formatCurrency(currentKPIs.expenseArvio) }}
              <span
                class="ml-1"
                :class="
                  isExpenseWithinBudget(
                    currentKPIs.expenseArvio,
                    currentKPIs.expenseToteutuma,
                  )
                    ? 'green--text font-weight-bold'
                    : 'red--text font-weight-bold'
                "
              >
                (Jäljellä {{ formatCurrency(currentKPIs.expenseErotus) }})
              </span>
            </div>
          </v-card>
        </v-col>

        <!-- 3. TULOS / NETTO -->
        <v-col cols="12" sm="6" md="3">
          <v-card
            outlined
            class="pa-4 text-center"
            :class="
              currentKPIs.netToteutuma >= 0
                ? 'border-left-primary'
                : 'border-left-orange'
            "
          >
            <div
              class="text-caption text-uppercase font-weight-bold d-flex align-center justify-center"
              :class="
                currentKPIs.netToteutuma >= 0
                  ? 'primary--text'
                  : 'deep-orange--text text--darken-2'
              "
            >
              <v-icon
                left
                x-small
                :color="currentKPIs.netToteutuma >= 0 ? 'primary' : 'deep-orange'"
                >mdi-scale-balance</v-icon
              >
              Tulos / Netto
            </div>
            <div
              class="text-h5 font-weight-black mt-1"
              :class="
                currentKPIs.netToteutuma >= 0
                  ? 'green--text text--darken-2'
                  : 'red--text text--darken-2'
              "
            >
              {{ formatCurrency(currentKPIs.netToteutuma) }}
            </div>
            <div class="text-caption grey--text mt-1">
              Arvioitu tulos: {{ formatCurrency(currentKPIs.netArvio) }}
            </div>
          </v-card>
        </v-col>

        <!-- 4. KULUTUSASTE -->
        <v-col cols="12" sm="6" md="3">
          <v-card outlined class="pa-4 text-center">
            <div
              class="text-caption grey--text text--darken-2 text-uppercase font-weight-bold d-flex align-center justify-center"
            >
              <v-icon left x-small color="grey darken-2">mdi-chart-donut</v-icon>
              Kulutusaste
            </div>
            <div
              class="text-h5 font-weight-black mt-1"
              :class="getUsageColor(currentKPIs.expenseUsage)"
            >
              {{ currentKPIs.expenseUsage }} %
            </div>
            <v-progress-linear
              :value="Math.min(currentKPIs.expenseUsage, 100)"
              :color="getUsageProgressColor(currentKPIs.expenseUsage)"
              height="6"
              rounded
              class="mt-2"
            ></v-progress-linear>
          </v-card>
        </v-col>
      </v-row>

      <!-- TAB 1: Consolidated Overview (Yhteenveto) -->
      <div v-if="activeTab === 'overview'">
        <v-card class="mb-4">
          <v-card-title class="d-flex justify-space-between align-center">
            <span>Kustannuspaikkojen yhteenveto {{ selectedYear }}</span>
            <span class="text-caption grey--text"
              >Klikkaa riviä siirtyäksesi kustannuspaikkaan</span
            >
          </v-card-title>
          <v-data-table
            :headers="overviewHeaders"
            :items="overviewItems"
            hide-default-footer
            disable-pagination
            class="elevation-0"
            @click:row="goToProject"
            style="cursor: pointer"
          >
            <template v-slot:item.codeName="{ item }">
              <strong>{{ item.code }}</strong> {{ item.name }}
            </template>
            <template v-slot:item.incomeDisplay="{ item }">
              <span class="green--text font-weight-bold">
                {{ formatCurrency(item.incomeToteutuma) }}
              </span>
              <span class="text-caption grey--text">
                / {{ formatCurrency(item.incomeArvio) }}</span
              >
            </template>
            <template v-slot:item.expenseDisplay="{ item }">
              <span class="red--text font-weight-bold">
                {{ formatCurrency(item.expenseToteutuma) }}
              </span>
              <span class="text-caption grey--text">
                / {{ formatCurrency(item.expenseArvio) }}</span
              >
            </template>
            <template v-slot:item.netDisplay="{ item }">
              <span
                :class="
                  item.netToteutuma >= 0
                    ? 'green--text font-weight-bold'
                    : 'red--text font-weight-bold'
                "
              >
                {{ formatCurrency(item.netToteutuma) }}
              </span>
              <span class="text-caption grey--text">
                (Arv: {{ formatCurrency(item.netArvio) }})</span
              >
            </template>
            <template v-slot:item.kayttoAste="{ item }">
              <div class="d-flex align-center">
                <v-progress-linear
                  :value="Math.min(item.kayttoAste, 100)"
                  :color="getUsageProgressColor(item.kayttoAste)"
                  height="8"
                  rounded
                  style="max-width: 100px"
                  class="mr-2"
                ></v-progress-linear>
                <span class="text-caption font-weight-bold"
                  >{{ item.kayttoAste }}%</span
                >
              </div>
            </template>
          </v-data-table>
        </v-card>
      </div>

      <!-- TAB 2: KP-tapahtumat (Kirjanpidon tuodut kulut ja tuotot) -->
      <div v-else-if="activeTab === 'transactions'">
        <budget-transactions-list
          :transactions="transactions"
          :projects="budgetData ? budgetData.projects : []"
          :can-manage="canManage"
          :selected-year="selectedYear"
          :initial-project-id="transactionFilterProjectId"
          @open-import="showCsvImportDialog = true"
        />
      </div>

      <!-- TAB 3+: Individual Cost Center View (Tuotot ja Kulut eriteltynä) -->
      <div v-else-if="currentProject">
        <!-- Project Summary Banner -->
        <v-card outlined class="mb-4 pa-3 grey lighten-5">
          <v-row dense align="center">
            <v-col cols="12" sm="3" class="text-center text-sm-left">
              <div class="text-subtitle-1 font-weight-black primary--text">
                {{ currentProject.fullName }}
              </div>
              <div class="text-caption grey--text">
                {{ currentProject.accounts ? currentProject.accounts.length : 0 }} tiliä yhteensä
              </div>
            </v-col>
            <v-col cols="12" sm="9" class="d-flex justify-end align-center flex-wrap">
              <v-chip
                v-if="projectBalanceTotals.count > 0"
                outlined
                color="teal darken-2"
                class="mr-2 mb-1"
              >
                <v-icon left x-small>mdi-scale-balance</v-icon>
                Tase: {{ formatCurrency(projectBalanceTotals.toteutuma) }}
                (Arvio: {{ formatCurrency(projectBalanceTotals.arvio) }})
              </v-chip>
              <v-chip outlined color="green darken-2" class="mr-2 mb-1">
                <v-icon left x-small>mdi-cash-plus</v-icon>
                Tuotot: {{ formatCurrency(projectIncomeTotals.toteutuma) }}
                (Arvio: {{ formatCurrency(projectIncomeTotals.arvio) }})
              </v-chip>
              <v-chip outlined color="red darken-2" class="mr-2 mb-1">
                <v-icon left x-small>mdi-cash-minus</v-icon>
                Kulut: {{ formatCurrency(projectExpenseTotals.toteutuma) }}
                (Arvio: {{ formatCurrency(projectExpenseTotals.arvio) }})
              </v-chip>
              <v-chip
                v-if="projectInvestmentTotals.count > 0"
                outlined
                color="blue-grey darken-2"
                class="mr-2 mb-1"
              >
                <v-icon left x-small>mdi-layers-triple</v-icon>
                Investoinnit: {{ formatCurrency(projectInvestmentTotals.toteutuma) }}
                (Arvio: {{ formatCurrency(projectInvestmentTotals.arvio) }})
              </v-chip>
              <v-chip
                dark
                :color="projectNetTotals.toteutuma >= 0 ? 'green darken-2' : 'red darken-2'"
                class="mb-1 mr-2 font-weight-bold"
              >
                <v-icon left x-small>mdi-scale-balance</v-icon>
                Tulos: {{ formatCurrency(projectNetTotals.toteutuma) }}
              </v-chip>
              <v-btn
                small
                outlined
                color="primary"
                class="mb-1"
                @click="viewProjectTransactions(currentProject.id)"
                title="Näytä tämän osaston tuodut KP-tapahtumat"
              >
                <v-icon left small>mdi-receipt-text-outline</v-icon>
                KP-tapahtumat
                <v-chip
                  v-if="currentProjectTxCount > 0"
                  x-small
                  class="ml-1 font-weight-bold"
                  color="primary"
                  text-color="white"
                >
                  {{ currentProjectTxCount }}
                </v-chip>
              </v-btn>
            </v-col>
          </v-row>
        </v-card>

        <!-- Filters & Search Bar -->
        <v-card class="mb-4">
          <v-card-text class="py-3">
            <v-row dense align="center">
              <v-col cols="12" md="4">
                <v-text-field
                  v-model="accountSearch"
                  placeholder="Hae tilinumerolla tai nimellä..."
                  prepend-inner-icon="mdi-magnify"
                  outlined
                  dense
                  clearable
                  hide-details
                ></v-text-field>
              </v-col>

              <v-col cols="12" md="5" class="text-center">
                <v-btn-toggle
                  v-model="activeSectionFilter"
                  mandatory
                  dense
                  borderless
                >
                  <v-btn value="all" small>
                    Kaikki
                  </v-btn>
                  <v-btn
                    v-if="filteredBalanceAccounts.length > 0"
                    value="balance"
                    small
                    color="teal lighten-5 teal--text text--darken-4"
                  >
                    <v-icon left x-small color="teal darken-3">mdi-scale-balance</v-icon>
                    Tase ({{ filteredBalanceAccounts.length }})
                  </v-btn>
                  <v-btn value="income" small color="green lighten-5 green--text text--darken-3">
                    <v-icon left x-small color="green darken-2">mdi-cash-plus</v-icon>
                    Tuotot ({{ filteredIncomeAccounts.length }})
                  </v-btn>
                  <v-btn value="expense" small color="red lighten-5 red--text text--darken-3">
                    <v-icon left x-small color="red darken-2">mdi-cash-minus</v-icon>
                    Kulut ({{ filteredExpenseAccounts.length }})
                  </v-btn>
                  <v-btn
                    v-if="filteredInvestmentAccounts.length > 0"
                    value="investment"
                    small
                    color="blue-grey lighten-5 blue-grey--text text--darken-3"
                  >
                    <v-icon left x-small color="blue-grey darken-2">mdi-layers-triple</v-icon>
                    Investoinnit ({{ filteredInvestmentAccounts.length }})
                  </v-btn>
                </v-btn-toggle>
              </v-col>

              <v-col
                cols="12"
                md="3"
                class="d-flex justify-end align-center flex-wrap"
              >
                <v-chip-group
                  v-model="selectedCategoryFilter"
                  mandatory
                  active-class="primary--text"
                >
                  <v-chip small value="all">Kaikki ryhmät</v-chip>
                  <v-chip small value="Materiaalit ja palvelut">Materiaalit</v-chip>
                  <v-chip small value="Muut kulut">Muut kulut</v-chip>
                  <v-chip small value="Investoinnit">Investoinnit</v-chip>
                </v-chip-group>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <!-- 1. TASE (Oma pääoma) - Näytetään ennen tuottoja -->
        <v-card
          v-if="filteredBalanceAccounts.length > 0 && (activeSectionFilter === 'all' || activeSectionFilter === 'balance')"
          class="mb-4"
        >
          <v-card-title class="teal lighten-5 teal--text text--darken-4 py-2 d-flex justify-space-between align-center">
            <div class="d-flex align-center">
              <v-icon left color="teal darken-3">mdi-scale-balance</v-icon>
              <span class="font-weight-bold">Tase (Oma pääoma)</span>
              <v-chip x-small color="teal darken-3" dark class="ml-2 font-weight-bold">
                {{ filteredBalanceAccounts.length }} tiliä
              </v-chip>
            </div>
            <div
              v-if="projectBalanceTotals.arvio !== 0 || projectBalanceTotals.toteutuma !== 0"
              class="text-caption teal--text text--darken-4 font-weight-bold"
            >
              Arvio: {{ formatCurrency(projectBalanceTotals.arvio) }} |
              Toteutuma: {{ formatCurrency(projectBalanceTotals.toteutuma) }} |
              <span>
                Erotus: {{ formatCurrency(projectBalanceTotals.erotus) }}
              </span>
            </div>
          </v-card-title>

          <v-data-table
            :headers="accountHeaders"
            :items="filteredBalanceAccounts"
            :search="accountSearch"
            :item-class="getRowClass"
            disable-pagination
            hide-default-footer
            class="elevation-0"
            no-data-text="Ei tasetilejä tällä kustannuspaikalla"
          >
            <template v-slot:item.accountNumber="{ item }">
              <span class="font-weight-bold font-mono">{{
                item.accountNumber || "-"
              }}</span>
            </template>

            <template v-slot:item.subAccount="{ item }">
              <span class="font-mono grey--text text--darken-2" v-if="item.subAccount">
                {{ item.subAccount }}
              </span>
              <span v-else class="grey--text text-caption">-</span>
            </template>

            <template v-slot:item.accountName="{ item }">
              <div :class="{ 'font-weight-bold': !isAccountModifiable(item) }">
                {{ item.accountName }}
                <v-chip
                  v-if="item.isCustom"
                  x-small
                  color="info"
                  outlined
                  class="ml-1"
                >
                  Uusi
                </v-chip>
              </div>
              <div
                class="text-caption grey--text"
                v-if="item.category && isAccountModifiable(item)"
              >
                {{ item.category }}
              </div>
            </template>

            <template v-slot:item.arvio="{ item }">
              <span :class="{ 'font-weight-bold': !isAccountModifiable(item) }">
                {{ formatCurrency(item.arvio) }}
              </span>
            </template>

            <template v-slot:item.totalToteutuma="{ item }">
              <span
                :class="{
                  'font-weight-bold': true,
                  'teal--text text--darken-3': item.totalToteutuma > 0 && isAccountModifiable(item),
                }"
              >
                {{ formatCurrency(item.totalToteutuma) }}
              </span>
            </template>

            <template v-slot:item.erotus="{ item }">
              <span :class="getAccountErotusColor(item)">
                {{ formatCurrency(item.erotus) }}
              </span>
            </template>

            <template v-slot:item.txCount="{ item }">
              <v-btn
                v-if="isAccountModifiable(item) && item.txCount > 0"
                small
                rounded
                depressed
                color="teal darken-2"
                dark
                @click="openAccountTransactions(item)"
              >
                <v-icon x-small left>mdi-receipt</v-icon>
                {{ item.txCount }} kpl
              </v-btn>
              <span v-else class="grey--text text-caption">-</span>
            </template>

            <template v-slot:item.actions="{ item }">
              <div class="d-flex align-center justify-center" v-if="canManage && isEditMode && !item.isTotal">
                <v-btn
                  icon
                  x-small
                  class="mr-1"
                  @click.stop="moveAccount(item, 'up')"
                  title="Siirrä ylemmäs"
                >
                  <v-icon small>mdi-chevron-up</v-icon>
                </v-btn>
                <v-btn
                  icon
                  x-small
                  class="mr-1"
                  @click.stop="moveAccount(item, 'down')"
                  title="Siirrä alemmas"
                >
                  <v-icon small>mdi-chevron-down</v-icon>
                </v-btn>
                <v-btn
                  icon
                  x-small
                  @click.stop="editAccount(item)"
                  :title="!isAccountModifiable(item) ? 'Muokkaa otsikkoriviä' : 'Muokkaa tiliä'"
                >
                  <v-icon small>mdi-pencil</v-icon>
                </v-btn>
              </div>
            </template>
          </v-data-table>
        </v-card>

        <!-- 2. TUOTOT -TAULUKKO -->
        <v-card
          v-if="activeSectionFilter === 'all' || activeSectionFilter === 'income'"
          class="mb-4"
        >
          <v-card-title class="green lighten-5 green--text text--darken-3 py-2 d-flex justify-space-between align-center">
            <div class="d-flex align-center">
              <v-icon left color="green darken-2">mdi-cash-plus</v-icon>
              <span class="font-weight-bold">Tuotot (Tulot)</span>
              <v-chip x-small color="green darken-2" dark class="ml-2 font-weight-bold">
                {{ filteredIncomeAccounts.length }} tiliä
              </v-chip>
            </div>
            <div class="text-caption green--text text--darken-3 font-weight-bold">
              Arvio: {{ formatCurrency(projectIncomeTotals.arvio) }} |
              Toteutuma: {{ formatCurrency(projectIncomeTotals.toteutuma) }} |
              Erotus: {{ formatCurrency(-projectIncomeTotals.erotus) }}
            </div>
          </v-card-title>

          <v-data-table
            :headers="accountHeaders"
            :items="filteredIncomeAccounts"
            :search="accountSearch"
            :item-class="getRowClass"
            disable-pagination
            hide-default-footer
            class="elevation-0"
            no-data-text="Ei tuottotilejä tällä kustannuspaikalla"
          >
            <template v-slot:item.accountNumber="{ item }">
              <span class="font-weight-bold font-mono">{{
                item.accountNumber || "-"
              }}</span>
            </template>

            <template v-slot:item.subAccount="{ item }">
              <span class="font-mono grey--text text--darken-2" v-if="item.subAccount">
                {{ item.subAccount }}
              </span>
              <span v-else class="grey--text text-caption">-</span>
            </template>

            <template v-slot:item.accountName="{ item }">
              <div :class="{ 'font-weight-bold': !isAccountModifiable(item) }">
                {{ item.accountName }}
                <v-chip
                  v-if="item.isCustom"
                  x-small
                  color="info"
                  outlined
                  class="ml-1"
                >
                  Uusi
                </v-chip>
              </div>
              <div
                class="text-caption grey--text"
                v-if="item.category && isAccountModifiable(item)"
              >
                {{ item.category }}
              </div>
            </template>

            <template v-slot:item.arvio="{ item }">
              <span :class="{ 'font-weight-bold': !isAccountModifiable(item) }">
                {{ formatCurrency(item.arvio) }}
              </span>
            </template>

            <template v-slot:item.totalToteutuma="{ item }">
              <span
                :class="{
                  'font-weight-bold': true,
                  'green--text text--darken-2': item.totalToteutuma > 0,
                }"
              >
                {{ formatCurrency(item.totalToteutuma) }}
              </span>
            </template>

            <template v-slot:item.erotus="{ item }">
              <span
                :class="
                  item.erotus <= 0
                    ? 'green--text font-weight-bold'
                    : 'orange--text font-weight-bold'
                "
              >
                {{ formatCurrency(-item.erotus) }}
              </span>
            </template>

            <template v-slot:item.txCount="{ item }">
              <v-btn
                v-if="isAccountModifiable(item) && item.txCount > 0"
                small
                rounded
                depressed
                color="green darken-1"
                dark
                class="px-2"
                @click.stop="openAccountTransactions(item)"
              >
                <v-icon x-small left>mdi-receipt</v-icon>
                {{ item.txCount }} kpl
              </v-btn>
              <span v-else class="grey--text text-caption">-</span>
            </template>

            <template v-slot:item.actions="{ item }">
              <div class="d-flex align-center justify-center" v-if="canManage && isEditMode && !item.isTotal">
                <v-btn
                  icon
                  x-small
                  class="mr-1"
                  @click.stop="moveAccount(item, 'up')"
                  title="Siirrä ylemmäs"
                >
                  <v-icon small>mdi-chevron-up</v-icon>
                </v-btn>
                <v-btn
                  icon
                  x-small
                  class="mr-1"
                  @click.stop="moveAccount(item, 'down')"
                  title="Siirrä alemmas"
                >
                  <v-icon small>mdi-chevron-down</v-icon>
                </v-btn>
                <v-btn
                  icon
                  x-small
                  @click.stop="editAccount(item)"
                  :title="!isAccountModifiable(item) ? 'Muokkaa otsikkoriviä' : 'Muokkaa tiliä'"
                >
                  <v-icon small>mdi-pencil</v-icon>
                </v-btn>
              </div>
            </template>
          </v-data-table>
        </v-card>

        <!-- 2. KULUT -TAULUKKO -->
        <v-card
          v-if="activeSectionFilter === 'all' || activeSectionFilter === 'expense'"
          class="mb-4"
        >
          <v-card-title class="red lighten-5 red--text text--darken-3 py-2 d-flex justify-space-between align-center">
            <div class="d-flex align-center">
              <v-icon left color="red darken-2">mdi-cash-minus</v-icon>
              <span class="font-weight-bold">Kulut (Menot)</span>
              <v-chip x-small color="red darken-2" dark class="ml-2 font-weight-bold">
                {{ filteredExpenseAccounts.length }} tiliä
              </v-chip>
            </div>
            <div class="text-caption red--text text--darken-3 font-weight-bold">
              Arvio: {{ formatCurrency(projectExpenseTotals.arvio) }} |
              Toteutuma: {{ formatCurrency(projectExpenseTotals.toteutuma) }} |
              <span
                :class="
                  isExpenseWithinBudget(
                    projectExpenseTotals.arvio,
                    projectExpenseTotals.toteutuma,
                  )
                    ? 'green--text text--darken-2 font-weight-bold'
                    : 'red--text text--darken-2 font-weight-bold'
                "
              >
                Jäljellä: {{ formatCurrency(projectExpenseTotals.erotus) }}
              </span>
            </div>
          </v-card-title>

          <v-data-table
            :headers="accountHeaders"
            :items="filteredExpenseAccounts"
            :search="accountSearch"
            :item-class="getRowClass"
            disable-pagination
            hide-default-footer
            class="elevation-0"
            no-data-text="Ei kulutilejä tällä kustannuspaikalla"
          >
            <template v-slot:item.accountNumber="{ item }">
              <span class="font-weight-bold font-mono">{{
                item.accountNumber || "-"
              }}</span>
            </template>

            <template v-slot:item.subAccount="{ item }">
              <span class="font-mono grey--text text--darken-2" v-if="item.subAccount">
                {{ item.subAccount }}
              </span>
              <span v-else class="grey--text text-caption">-</span>
            </template>

            <template v-slot:item.accountName="{ item }">
              <div :class="{ 'font-weight-bold': !isAccountModifiable(item) }">
                {{ item.accountName }}
                <v-chip
                  v-if="item.isCustom"
                  x-small
                  color="info"
                  outlined
                  class="ml-1"
                >
                  Uusi
                </v-chip>
              </div>
              <div
                class="text-caption grey--text"
                v-if="item.category && isAccountModifiable(item)"
              >
                {{ item.category }}
              </div>
            </template>

            <template v-slot:item.arvio="{ item }">
              <span :class="{ 'font-weight-bold': !isAccountModifiable(item) }">
                {{ formatCurrency(item.arvio) }}
              </span>
            </template>

            <template v-slot:item.totalToteutuma="{ item }">
              <span
                :class="{
                  'font-weight-bold': true,
                  'red--text text--darken-2': item.totalToteutuma !== 0,
                }"
              >
                {{ formatCurrency(item.totalToteutuma) }}
              </span>
            </template>

            <template v-slot:item.erotus="{ item }">
              <span :class="getAccountErotusColor(item)">
                {{ formatCurrency(item.erotus) }}
              </span>
            </template>

            <template v-slot:item.txCount="{ item }">
              <v-btn
                v-if="isAccountModifiable(item) && item.txCount > 0"
                small
                rounded
                depressed
                color="red darken-1"
                dark
                class="px-2"
                @click.stop="openAccountTransactions(item)"
              >
                <v-icon x-small left>mdi-receipt</v-icon>
                {{ item.txCount }} kpl
              </v-btn>
              <span v-else class="grey--text text-caption">-</span>
            </template>

            <template v-slot:item.actions="{ item }">
              <div class="d-flex align-center justify-center" v-if="canManage && isEditMode && !item.isTotal">
                <v-btn
                  icon
                  x-small
                  class="mr-1"
                  @click.stop="moveAccount(item, 'up')"
                  title="Siirrä ylemmäs"
                >
                  <v-icon small>mdi-chevron-up</v-icon>
                </v-btn>
                <v-btn
                  icon
                  x-small
                  class="mr-1"
                  @click.stop="moveAccount(item, 'down')"
                  title="Siirrä alemmas"
                >
                  <v-icon small>mdi-chevron-down</v-icon>
                </v-btn>
                <v-btn
                  icon
                  x-small
                  @click.stop="editAccount(item)"
                  :title="!isAccountModifiable(item) ? 'Muokkaa otsikkoriviä' : 'Muokkaa tiliä'"
                >
                  <v-icon small>mdi-pencil</v-icon>
                </v-btn>
              </div>
            </template>
          </v-data-table>
        </v-card>

        <!-- 4. INVESTOINNIT (Ryhmiteltynä XX1, XX2, XX3...) -->
        <div
          v-if="filteredInvestmentAccounts.length > 0 && (activeSectionFilter === 'all' || activeSectionFilter === 'investment')"
          class="mb-4"
        >
          <!-- Pääkortti: Investointien yhteenveto ja toiminnot -->
          <v-card class="mb-3">
            <v-card-title class="blue-grey lighten-5 blue-grey--text text--darken-3 py-2 d-flex justify-space-between align-center flex-wrap">
              <div class="d-flex align-center flex-wrap">
                <v-icon left color="blue-grey darken-2">mdi-layers-triple</v-icon>
                <span class="font-weight-bold">Investoinnit</span>
                <v-chip x-small color="blue-grey darken-2" dark class="ml-2 font-weight-bold">
                  {{ filteredInvestmentAccounts.length }} tiliä ({{ investmentGroups.length }} kohdetta)
                </v-chip>
                <v-btn
                  x-small
                  text
                  color="blue-grey darken-3"
                  class="ml-3 text-none"
                  @click="expandAllInvestments"
                >
                  <v-icon x-small left>mdi-chevron-down</v-icon>
                  Avaa kaikki
                </v-btn>
                <v-btn
                  x-small
                  text
                  color="blue-grey darken-3"
                  class="ml-1 text-none"
                  @click="collapseAllInvestments"
                >
                  <v-icon x-small left>mdi-chevron-up</v-icon>
                  Sulje kaikki
                </v-btn>
              </div>
              <div
                v-if="projectInvestmentTotals.arvio !== 0 || projectInvestmentTotals.toteutuma !== 0"
                class="text-caption blue-grey--text text--darken-3 font-weight-bold"
              >
                Arvio yhteensä: {{ formatCurrency(projectInvestmentTotals.arvio) }} |
                Toteutuma yhteensä: {{ formatCurrency(projectInvestmentTotals.toteutuma) }} |
                <span>
                  Jäljellä: {{ formatCurrency(projectInvestmentTotals.erotus) }}
                </span>
              </div>
            </v-card-title>
          </v-card>

          <!-- Investointikohtaiset ryhmät (XX1, XX2, XX3...) -->
          <v-card
            v-for="group in filteredInvestmentGroups"
            :key="group.id"
            class="mb-3"
            outlined
          >
            <!-- Investoinnin otsikko (XX1, XX2, ...) -->
            <v-card-title
              class="blue-grey lighten-4 blue-grey--text text--darken-4 py-2 px-4 d-flex justify-space-between align-center flex-wrap"
              style="cursor: pointer;"
              @click="toggleGroup(group.id)"
            >
              <div class="d-flex align-center flex-wrap">
                <v-btn icon x-small class="mr-1" color="blue-grey darken-3">
                  <v-icon small>
                    {{ collapsedGroups[group.id] ? "mdi-chevron-right" : "mdi-chevron-down" }}
                  </v-icon>
                </v-btn>
                <span class="font-weight-bold font-mono mr-2">{{ group.code }}</span>
                <span class="font-weight-bold text-subtitle-1">{{ group.title }}</span>
                <v-chip x-small class="ml-2 font-weight-bold" color="blue-grey darken-2" dark>
                  {{ group.accounts.length }} riviä
                </v-chip>
                <v-btn
                  v-if="canManage && isEditMode && group.headerAccount"
                  icon
                  x-small
                  class="ml-2"
                  @click.stop="moveInvestmentGroup(group, 'up')"
                  title="Siirrä investointikohde ylemmäs"
                >
                  <v-icon small>mdi-chevron-up</v-icon>
                </v-btn>
                <v-btn
                  v-if="canManage && isEditMode && group.headerAccount"
                  icon
                  x-small
                  class="ml-1"
                  @click.stop="moveInvestmentGroup(group, 'down')"
                  title="Siirrä investointikohde alemmas"
                >
                  <v-icon small>mdi-chevron-down</v-icon>
                </v-btn>
                <v-btn
                  v-if="canManage && isEditMode && group.headerAccount"
                  icon
                  x-small
                  class="ml-1"
                  @click.stop="editAccount(group.headerAccount)"
                  title="Muokkaa investoinnin tietoja"
                >
                  <v-icon small>mdi-pencil</v-icon>
                </v-btn>
              </div>
              <div class="text-caption blue-grey--text text--darken-4 font-weight-bold d-flex align-center flex-wrap">
                <span class="mr-3">Arvio: {{ formatCurrency(group.arvio) }}</span>
                <span class="mr-3">Toteutuma: {{ formatCurrency(group.toteutuma) }}</span>
                <span>
                  Jäljellä / Erotus:
                  <span :class="group.erotus >= 0 ? 'green--text text--darken-3' : 'red--text text--darken-3'">
                    {{ formatCurrency(group.erotus) }}
                  </span>
                </span>
              </div>
            </v-card-title>

            <!-- Investoinnin rivit -->
            <v-expand-transition>
              <div v-show="!collapsedGroups[group.id]">
                <v-data-table
                  :headers="accountHeaders"
                  :items="group.accounts"
                  :search="groupTitleMatches(group) ? '' : accountSearch"
                  :item-class="getRowClass"
                  disable-pagination
                  hide-default-footer
                  dense
                  class="elevation-0"
                  no-data-text="Ei rivejä tälle investoinnille"
                >
                  <template v-slot:item.accountNumber="{ item }">
                    <span class="font-weight-bold font-mono">{{
                      item.accountNumber || "-"
                    }}</span>
                  </template>

                  <template v-slot:item.subAccount="{ item }">
                    <span
                      class="font-mono grey--text text--darken-2"
                      v-if="item.subAccount"
                    >
                      {{ item.subAccount }}
                    </span>
                    <span v-else class="grey--text text-caption">-</span>
                  </template>

                  <template v-slot:item.accountName="{ item }">
                    <div :class="{ 'font-weight-bold': !isAccountModifiable(item) }">
                      {{ item.accountName }}
                      <v-chip
                        v-if="item.isCustom"
                        x-small
                        color="info"
                        outlined
                        class="ml-1"
                      >
                        Uusi
                      </v-chip>
                    </div>
                  </template>

                  <template v-slot:item.arvio="{ item }">
                    <span :class="{ 'font-weight-bold': !isAccountModifiable(item) }">
                      {{ formatCurrency(item.arvio) }}
                    </span>
                  </template>

                  <template v-slot:item.totalToteutuma="{ item }">
                    <span
                      :class="{
                        'font-weight-bold': true,
                        'red--text text--darken-2': item.totalToteutuma < 0 && isAccountModifiable(item),
                        'teal--text text--darken-3': item.totalToteutuma > 0 && isAccountModifiable(item),
                      }"
                    >
                      {{ formatCurrency(item.totalToteutuma) }}
                    </span>
                  </template>

                  <template v-slot:item.erotus="{ item }">
                    <span :class="getAccountErotusColor(item)">
                      {{ formatCurrency(item.erotus) }}
                    </span>
                  </template>

                  <template v-slot:item.txCount="{ item }">
                    <v-btn
                      v-if="isAccountModifiable(item) && item.txCount > 0"
                      small
                      rounded
                      depressed
                      color="blue-grey"
                      dark
                      class="px-2"
                      @click.stop="openAccountTransactions(item)"
                    >
                      <v-icon x-small left>mdi-receipt</v-icon>
                      {{ item.txCount }} kpl
                    </v-btn>
                    <span v-else class="grey--text text-caption">-</span>
                  </template>

                  <template v-slot:item.actions="{ item }">
                    <div class="d-flex align-center justify-center" v-if="canManage && isEditMode && !item.isTotal">
                      <v-btn
                        icon
                        x-small
                        class="mr-1"
                        @click.stop="moveAccount(item, 'up')"
                        title="Siirrä ylemmäs"
                      >
                        <v-icon small>mdi-chevron-up</v-icon>
                      </v-btn>
                      <v-btn
                        icon
                        x-small
                        class="mr-1"
                        @click.stop="moveAccount(item, 'down')"
                        title="Siirrä alemmas"
                      >
                        <v-icon small>mdi-chevron-down</v-icon>
                      </v-btn>
                      <v-btn
                        icon
                        x-small
                        @click.stop="editAccount(item)"
                        :title="!isAccountModifiable(item) ? 'Muokkaa otsikkoriviä' : 'Muokkaa tiliä'"
                      >
                        <v-icon small>mdi-pencil</v-icon>
                      </v-btn>
                    </div>
                  </template>
                </v-data-table>
              </div>
            </v-expand-transition>
          </v-card>
        </div>
      </div>
    </div>

    <!-- DIALOG 1: CSV Import Wizard -->
    <budget-csv-import-dialog
      v-model="showCsvImportDialog"
      :year="selectedYear"
      :projects="budgetData ? budgetData.projects : []"
      :existing-transactions="transactions"
      @imported="onTransactionsImported"
    />

    <!-- DIALOG 2: Create / Import Budget Base -->
    <budget-create-dialog
      v-model="showCreateDialog"
      :current-year="selectedYear"
      :available-years="availableYears"
      @created="onBudgetCreated"
    />

    <!-- DIALOG 3: Add / Edit Budget Account -->
    <budget-account-edit-dialog
      v-model="showAccountEditDialog"
      :account="accountToEdit"
      :projects="budgetData ? budgetData.projects : []"
      :default-project-id="currentProjectId"
      @save="onAccountSaved"
      @delete="onAccountDeleted"
    />

    <!-- DIALOG 4: Account Transactions Details -->
    <budget-account-transactions-dialog
      v-model="showTransactionsDialog"
      :account="selectedAccountForTx"
      :project-id="currentProjectId"
      :transactions="transactions"
      :projects="budgetData ? budgetData.projects : []"
      :can-manage="canManage"
    />

    <!-- DIALOG 5: History, Changelog & Rollback -->
    <budget-history-dialog
      v-model="showHistoryDialog"
      :year="selectedYear"
      :can-manage="canManage"
      @restored="onVersionRestored"
    />
    </div>
  </component>
</template>

<script>
import BasicPage from "@/components/BasicPage";
import BudgetCsvImportDialog from "@/components/Budget/BudgetCsvImportDialog.vue";
import BudgetCreateDialog from "@/components/Budget/BudgetCreateDialog.vue";
import BudgetAccountEditDialog from "@/components/Budget/BudgetAccountEditDialog.vue";
import BudgetAccountTransactionsDialog from "@/components/Budget/BudgetAccountTransactionsDialog.vue";
import BudgetHistoryDialog from "@/components/Budget/BudgetHistoryDialog.vue";
import BudgetTransactionsList from "@/components/Budget/BudgetTransactionsList.vue";
import defaultBudget2026 from "@/assets/defaultBudget2026.json";

import {
  getAvailableBudgetYears,
  subscribeBudget,
  subscribeTransactions,
  saveBudget,
  addAccountToProject,
  updateAccountInProject,
  deleteAccountFromProject,
  moveAccountInProject,
} from "@/services/budgetService";

import * as XLSX from "xlsx";

export default {
  name: "Budjetti",
  components: {
    BasicPage,
    BudgetCsvImportDialog,
    BudgetCreateDialog,
    BudgetAccountEditDialog,
    BudgetAccountTransactionsDialog,
    BudgetHistoryDialog,
    BudgetTransactionsList,
  },
  props: {
    embedded: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      selectedYear: 2026,
      availableYears: [2026],
      activeTab: "overview",
      activeSectionFilter: "all", // "all" | "income" | "expense"
      loading: true,
      seeding: false,
      budgetData: null,
      transactions: [],
      budgetUnsub: null,
      txUnsub: null,

      // Dialog states
      showCsvImportDialog: false,
      showCreateDialog: false,
      showAccountEditDialog: false,
      showTransactionsDialog: false,
      showHistoryDialog: false,

      accountToEdit: null,
      selectedAccountForTx: null,

      // Filters
      accountSearch: "",
      selectedCategoryFilter: "all",
      transactionFilterProjectId: "all",
      collapsedGroups: {},

      overviewHeaders: [
        { text: "Kustannuspaikka", value: "codeName", sortable: true },
        {
          text: "Tuotot (Toteutuma / Arvio)",
          value: "incomeDisplay",
          align: "right",
          sortable: true,
        },
        {
          text: "Kulut (Toteutuma / Arvio)",
          value: "expenseDisplay",
          align: "right",
          sortable: true,
        },
        {
          text: "Tulos (Toteutuma / Arvio)",
          value: "netDisplay",
          align: "right",
          sortable: true,
        },
        {
          text: "Kulutusaste %",
          value: "kayttoAste",
          align: "left",
          sortable: true,
        },
      ],

      isEditMode: false,
    };
  },
  computed: {
    accountHeaders() {
      const headers = [
        {
          text: "Tilinro",
          value: "accountNumber",
          width: "90px",
          sortable: true,
        },
        {
          text: "Kirjanpidon tili",
          value: "subAccount",
          width: "120px",
          sortable: true,
        },
        { text: "Tilin nimi / Kuvaus", value: "accountName", sortable: true },
        { text: "Arvio (€)", value: "arvio", align: "right", sortable: true },
        {
          text: "Toteutuma (€)",
          value: "totalToteutuma",
          align: "right",
          sortable: true,
        },
        {
          text: "Jäljellä / Erotus (€)",
          value: "erotus",
          align: "right",
          sortable: true,
        },
        {
          text: "Tapahtumat",
          value: "txCount",
          align: "center",
          width: "110px",
          sortable: true,
        },
      ];

      if (this.canManage && this.isEditMode) {
        headers.push({
          text: "Toiminnot",
          value: "actions",
          align: "center",
          width: "110px",
          sortable: false,
        });
      }

      return headers;
    },
    canView() {
      const isJoniLeino =
        this.$isHPJ ||
        this.$userInfo?.email === "joni.leino88@gmail.com";
      return !!(
        this.$isHallitus ||
        this.$isTreasurer ||
        isJoniLeino ||
        this.$isSuperAdmin
      );
    },
    canManage() {
      const isJoniLeino =
        this.$isHPJ ||
        this.$userInfo?.email === "joni.leino88@gmail.com";
      return !!(
        this.$isTreasurer ||
        isJoniLeino ||
        this.$isSuperAdmin
      );
    },
    currentProjectId() {
      if (this.activeTab && this.activeTab.startsWith("proj-")) {
        return this.activeTab.replace("proj-", "");
      }
      return "";
    },
    currentProject() {
      if (!this.budgetData || !this.currentProjectId) return null;
      return this.budgetData.projects.find(
        (p) =>
          String(p.id) === String(this.currentProjectId) ||
          String(p.code) === String(this.currentProjectId),
      );
    },
    currentProjectTxCount() {
      if (!this.currentProjectId) return 0;
      return this.transactions.filter(
        (t) => String(t.projectId) === String(this.currentProjectId),
      ).length;
    },
    // Map of accountId -> assigned transactions sum & count
    txStatsByAccountId() {
      const stats = {};
      this.transactions.forEach((tx) => {
        if (tx.status === "assigned" && tx.accountId) {
          if (!stats[tx.accountId]) {
            stats[tx.accountId] = { count: 0, sum: 0 };
          }
          stats[tx.accountId].count += 1;
          let amt = parseFloat(tx.amount) || 0;
          if (
            amt > 0 &&
            (tx.tositelaji === "Ostolasku" || tx.tositelaji === "Kululasku")
          ) {
            amt = -amt;
          }
          stats[tx.accountId].sum += amt;
        }
      });
      return stats;
    },
    currentKPIs() {
      let incomeArvio = 0;
      let incomeToteutuma = 0;
      let expenseArvio = 0;
      let expenseToteutuma = 0;

      const targetProjects =
        this.activeTab === "overview" && this.budgetData
          ? this.budgetData.projects
          : this.currentProject
          ? [this.currentProject]
          : [];

      targetProjects.forEach((p) => {
        (p.accounts || []).forEach((a) => {
          if (!a.isTotal && !a.isTitle) {
            const arvio = a.arvio || 0;
            const stats = this.txStatsByAccountId[a.id] || { sum: 0 };
            const tot = (a.baseToteutuma || 0) + stats.sum;

            if (a.type === "income") {
              incomeArvio += arvio;
              incomeToteutuma += tot;
            } else if (a.type === "expense") {
              expenseArvio += arvio;
              expenseToteutuma += tot;
            }
          }
        });
      });

      const incomeErotus = incomeArvio - incomeToteutuma;
      const expenseErotus = expenseArvio - expenseToteutuma;
      const netArvio =
        expenseArvio < 0
          ? incomeArvio + expenseArvio
          : incomeArvio - expenseArvio;
      const netToteutuma =
        expenseToteutuma < 0
          ? incomeToteutuma + expenseToteutuma
          : incomeToteutuma - expenseToteutuma;
      const netErotus = netArvio - netToteutuma;

      let expenseUsage = 0;
      if (expenseArvio !== 0) {
        expenseUsage = Math.round(
          (Math.abs(expenseToteutuma) / Math.abs(expenseArvio)) * 100,
        );
      }

      return {
        incomeArvio: Math.round(incomeArvio * 100) / 100,
        incomeToteutuma: Math.round(incomeToteutuma * 100) / 100,
        incomeErotus: Math.round(incomeErotus * 100) / 100,
        expenseArvio: Math.round(expenseArvio * 100) / 100,
        expenseToteutuma: Math.round(expenseToteutuma * 100) / 100,
        expenseErotus: Math.round(expenseErotus * 100) / 100,
        netArvio: Math.round(netArvio * 100) / 100,
        netToteutuma: Math.round(netToteutuma * 100) / 100,
        netErotus: Math.round(netErotus * 100) / 100,
        expenseUsage,
      };
    },
    overviewItems() {
      if (!this.budgetData) return [];

      return this.budgetData.projects.map((p) => {
        let incomeArvio = 0;
        let incomeTot = 0;
        let expenseArvio = 0;
        let expenseTot = 0;

        (p.accounts || []).forEach((a) => {
          if (!a.isTotal && !a.isTitle) {
            const arvio = a.arvio || 0;
            const stats = this.txStatsByAccountId[a.id] || { sum: 0 };
            const tot = (a.baseToteutuma || 0) + stats.sum;

            if (a.type === "income") {
              incomeArvio += arvio;
              incomeTot += tot;
            } else if (a.type === "expense") {
              expenseArvio += arvio;
              expenseTot += tot;
            }
          }
        });

        const netArvio =
          expenseArvio < 0
            ? incomeArvio + expenseArvio
            : incomeArvio - expenseArvio;
        const netToteutuma =
          expenseTot < 0
            ? incomeTot + expenseTot
            : incomeTot - expenseTot;
        let kayttoAste = 0;
        if (expenseArvio !== 0) {
          kayttoAste = Math.round(
            (Math.abs(expenseTot) / Math.abs(expenseArvio)) * 100,
          );
        }

        return {
          id: p.id,
          code: p.code,
          name: p.name,
          incomeArvio: Math.round(incomeArvio * 100) / 100,
          incomeToteutuma: Math.round(incomeTot * 100) / 100,
          incomeErotus: Math.round((incomeArvio - incomeTot) * 100) / 100,
          expenseArvio: Math.round(expenseArvio * 100) / 100,
          expenseToteutuma: Math.round(expenseTot * 100) / 100,
          expenseErotus: Math.round((expenseArvio - expenseTot) * 100) / 100,
          netArvio: Math.round(netArvio * 100) / 100,
          netToteutuma: Math.round(netToteutuma * 100) / 100,
          kayttoAste,
        };
      });
    },
    allProjectAccounts() {
      if (!this.currentProject || !this.currentProject.accounts) return [];

      const baseAccounts = this.currentProject.accounts.map((a) => {
        const stats = this.txStatsByAccountId[a.id] || { count: 0, sum: 0 };
        const base = a.baseToteutuma || 0;
        const totalToteutuma = base + stats.sum;
        const erotus = (a.arvio || 0) - totalToteutuma;

        let type = a.type;
        const num = String(a.accountNumber || "").trim();
        const cat = String(a.category || "").toLowerCase();
        if (
          a.type !== "investment" &&
          !cat.includes("investoin") &&
          (type === "balance" ||
            type === "equity" ||
            num.startsWith("1") ||
            num.startsWith("2") ||
            cat.includes("tase") ||
            cat.includes("pääoma") ||
            cat.includes("voitto (tappio)"))
        ) {
          if (
            !num.startsWith("3") &&
            !num.startsWith("4") &&
            !num.startsWith("X")
          ) {
            type = "balance";
          }
        }

        return {
          ...a,
          type,
          projectId: this.currentProjectId,
          txCount: stats.count,
          txSum: stats.sum,
          totalToteutuma,
          erotus,
        };
      });

      return this.calculateTitleSums(baseAccounts);
    },
    filteredBalanceAccounts() {
      return this.filterAccountList(
        this.allProjectAccounts.filter((a) => a.type === "balance"),
      );
    },
    filteredIncomeAccounts() {
      return this.filterAccountList(
        this.allProjectAccounts.filter((a) => a.type === "income"),
      );
    },
    filteredExpenseAccounts() {
      return this.filterAccountList(
        this.allProjectAccounts.filter((a) => a.type === "expense"),
      );
    },
    filteredInvestmentAccounts() {
      return this.filterAccountList(
        this.allProjectAccounts.filter(
          (a) =>
            a.type === "investment" ||
            (typeof a.accountNumber === "string" &&
              a.accountNumber.startsWith("X")),
        ),
      );
    },
    investmentGroups() {
      const accounts = this.allProjectAccounts.filter(
        (a) =>
          a.type === "investment" ||
          (typeof a.accountNumber === "string" &&
            a.accountNumber.startsWith("X")),
      );

      const groups = [];
      let currentGroup = null;

      for (const a of accounts) {
        const num = String(a.accountNumber || "").trim();
        const isInvHeader =
          /^XX\d+/i.test(num) ||
          (/^XX[0-9A-Za-z]/i.test(num) && !num.startsWith("XXX"));

        if (isInvHeader) {
          currentGroup = {
            id: a.id || `inv_${num}`,
            code: num,
            title: a.accountName || "",
            headerAccount: a,
            accounts: [],
          };
          groups.push(currentGroup);
        } else if (
          num === "XXX" &&
          a.accountName &&
          a.accountName.toLowerCase().includes("investoin")
        ) {
          continue;
        } else {
          if (!currentGroup) {
            currentGroup = {
              id: "inv_general",
              code: "Investoinnit",
              title: "Muut investoinnit",
              headerAccount: null,
              accounts: [],
            };
            groups.push(currentGroup);
          }
          currentGroup.accounts.push(a);
        }
      }

      return groups.map((g) => {
        let arvio = 0;
        let toteutuma = 0;
        g.accounts.forEach((acc) => {
          if (!acc.isTotal && !acc.isTitle) {
            arvio += acc.arvio || 0;
            toteutuma += acc.totalToteutuma || 0;
          }
        });

        const filteredAccounts = this.filterAccountList(g.accounts);

        return {
          ...g,
          accounts: filteredAccounts,
          totalAccountsCount: g.accounts.length,
          arvio: Math.round(arvio * 100) / 100,
          toteutuma: Math.round(toteutuma * 100) / 100,
          erotus: Math.round((arvio - toteutuma) * 100) / 100,
        };
      });
    },
    filteredInvestmentGroups() {
      const search = (this.accountSearch || "").trim().toLowerCase();
      if (!search) {
        return this.investmentGroups.filter((g) => g.accounts.length > 0);
      }

      return this.investmentGroups.filter((g) => {
        if (
          g.code.toLowerCase().includes(search) ||
          g.title.toLowerCase().includes(search)
        ) {
          return true;
        }
        return g.accounts.some((a) => {
          const num = String(a.accountNumber || "").toLowerCase();
          const name = String(a.accountName || "").toLowerCase();
          const sub = String(a.subAccount || "").toLowerCase();
          const cat = String(a.category || "").toLowerCase();
          return (
            num.includes(search) ||
            name.includes(search) ||
            sub.includes(search) ||
            cat.includes(search)
          );
        });
      });
    },
    filteredOtherAccounts() {
      return this.filteredInvestmentAccounts;
    },
    projectBalanceTotals() {
      let arvio = 0;
      let toteutuma = 0;
      this.filteredBalanceAccounts.forEach((a) => {
        if (!a.isTotal && !a.isTitle) {
          arvio += a.arvio || 0;
          toteutuma += a.totalToteutuma || 0;
        }
      });
      return {
        count: this.filteredBalanceAccounts.length,
        arvio: Math.round(arvio * 100) / 100,
        toteutuma: Math.round(toteutuma * 100) / 100,
        erotus: Math.round((arvio - toteutuma) * 100) / 100,
      };
    },
    projectIncomeTotals() {
      let arvio = 0;
      let toteutuma = 0;
      this.filteredIncomeAccounts.forEach((a) => {
        if (!a.isTotal && !a.isTitle) {
          arvio += a.arvio || 0;
          toteutuma += a.totalToteutuma || 0;
        }
      });
      return {
        count: this.filteredIncomeAccounts.length,
        arvio: Math.round(arvio * 100) / 100,
        toteutuma: Math.round(toteutuma * 100) / 100,
        erotus: Math.round((arvio - toteutuma) * 100) / 100,
      };
    },
    projectExpenseTotals() {
      let arvio = 0;
      let toteutuma = 0;
      this.filteredExpenseAccounts.forEach((a) => {
        if (!a.isTotal && !a.isTitle) {
          arvio += a.arvio || 0;
          toteutuma += a.totalToteutuma || 0;
        }
      });
      return {
        count: this.filteredExpenseAccounts.length,
        arvio: Math.round(arvio * 100) / 100,
        toteutuma: Math.round(toteutuma * 100) / 100,
        erotus: Math.round((arvio - toteutuma) * 100) / 100,
      };
    },
    projectInvestmentTotals() {
      let arvio = 0;
      let toteutuma = 0;
      this.filteredInvestmentAccounts.forEach((a) => {
        if (!a.isTotal && !a.isTitle) {
          arvio += a.arvio || 0;
          toteutuma += a.totalToteutuma || 0;
        }
      });
      return {
        count: this.filteredInvestmentAccounts.length,
        arvio: Math.round(arvio * 100) / 100,
        toteutuma: Math.round(toteutuma * 100) / 100,
        erotus: Math.round((arvio - toteutuma) * 100) / 100,
      };
    },
    projectNetTotals() {
      const inc = this.projectIncomeTotals;
      const exp = this.projectExpenseTotals;
      const arvio =
        exp.arvio < 0 ? inc.arvio + exp.arvio : inc.arvio - exp.arvio;
      const toteutuma =
        exp.toteutuma < 0
          ? inc.toteutuma + exp.toteutuma
          : inc.toteutuma - exp.toteutuma;
      return {
        arvio: Math.round(arvio * 100) / 100,
        toteutuma: Math.round(toteutuma * 100) / 100,
        erotus: Math.round((arvio - toteutuma) * 100) / 100,
      };
    },
  },
  watch: {
    canView(val) {
      if (val && !this.budgetUnsub) {
        this.fetchYears().then(() => {
          this.initRealtimeSubscriptions();
        });
      }
    },
  },
  async mounted() {
    if (!this.canView) return;
    await this.fetchYears();
    this.initRealtimeSubscriptions();
  },
  beforeDestroy() {
    this.cleanupSubscriptions();
  },
  methods: {
    filterAccountList(accounts) {
      if (this.selectedCategoryFilter === "all") return accounts;
      return accounts.filter(
        (a) =>
          a.category &&
          a.category
            .toLowerCase()
            .includes(this.selectedCategoryFilter.toLowerCase()),
      );
    },
    async fetchYears() {
      try {
        const years = await getAvailableBudgetYears();
        this.availableYears = years;
        if (!this.availableYears.includes(this.selectedYear)) {
          this.selectedYear = this.availableYears[0] || 2026;
        }
      } catch (err) {
        console.error("Error fetching years:", err);
      }
    },
    initRealtimeSubscriptions() {
      this.cleanupSubscriptions();
      this.loading = true;

      // 1. Subscribe to Budget Document
      this.budgetUnsub = subscribeBudget(this.selectedYear, (budget) => {
        this.budgetData = budget;
        this.loading = false;
      });

      // 2. Subscribe to Transactions for this year
      this.txUnsub = subscribeTransactions(this.selectedYear, (txs) => {
        this.transactions = txs;
      });
    },
    cleanupSubscriptions() {
      if (this.budgetUnsub) {
        this.budgetUnsub();
        this.budgetUnsub = null;
      }
      if (this.txUnsub) {
        this.txUnsub();
        this.txUnsub = null;
      }
    },
    onYearChange() {
      this.initRealtimeSubscriptions();
    },
    async seed2026Budget() {
      if (!this.canManage) return;
      this.seeding = true;
      try {
        await saveBudget(2026, defaultBudget2026, {
          actionType: "CREATE",
          summary: "Vuoden 2026 budjetti alustettu pohjasta (1-Budjetti 2026.xlsx)",
        });
        this.$toast?.success?.("Vuoden 2026 budjetti alustettu onnistuneesti!");
      } catch (err) {
        console.error("Seeding error:", err);
        this.$toast?.error?.("Alustus epäonnistui");
      } finally {
        this.seeding = false;
      }
    },
    goToProject(item) {
      this.activeTab = `proj-${item.id}`;
    },
    viewProjectTransactions(projectId) {
      this.transactionFilterProjectId = String(projectId);
      this.activeTab = "transactions";
    },
    openAddAccount() {
      this.accountToEdit = null;
      this.showAccountEditDialog = true;
    },
    openAddTitle() {
      this.accountToEdit = {
        isTitle: true,
        isHeader: true,
        projectId: this.currentProjectId,
        accountNumber: "",
        accountName: "",
        category: "Materiaalit ja palvelut",
        type: "expense",
      };
      this.showAccountEditDialog = true;
    },
    editAccount(account) {
      this.accountToEdit = { ...account };
      this.showAccountEditDialog = true;
    },
    async onAccountSaved(accountForm) {
      if (!this.canManage) return;
      const isTitle = Boolean(accountForm.isTitle || accountForm.isHeader);
      try {
        if (accountForm.id) {
          await updateAccountInProject(
            this.selectedYear,
            accountForm.projectId,
            accountForm.id,
            accountForm,
          );
          this.$toast?.success?.(
            isTitle ? "Otsikkorivi päivitetty!" : "Tili päivitetty!",
          );
        } else {
          await addAccountToProject(
            this.selectedYear,
            accountForm.projectId,
            accountForm,
          );
          this.$toast?.success?.(
            isTitle ? "Uusi otsikkorivi lisätty!" : "Uusi tili lisätty!",
          );
        }
      } catch (err) {
        console.error("Error saving account:", err);
        this.$toast?.error?.(err.message || "Tallennus epäonnistui");
      }
    },
    async onAccountDeleted({ projectId, accountId, isTitle }) {
      if (!this.canManage) return;
      try {
        await deleteAccountFromProject(this.selectedYear, projectId, accountId);
        this.$toast?.success?.(
          isTitle ? "Otsikkorivi poistettu!" : "Tili poistettu!",
        );
      } catch (err) {
        console.error("Error deleting account:", err);
        this.$toast?.error?.("Poisto epäonnistui");
      }
    },
    async moveAccount(item, direction) {
      if (!this.canManage || !item || !item.id) return;
      try {
        await moveAccountInProject(
          this.selectedYear,
          this.currentProjectId,
          item.id,
          direction,
        );
      } catch (err) {
        console.error("Error moving account:", err);
        this.$toast?.error?.("Siirto epäonnistui");
      }
    },
    async moveInvestmentGroup(group, direction) {
      if (!this.canManage || !group || !group.headerAccount) return;
      try {
        await moveAccountInProject(
          this.selectedYear,
          this.currentProjectId,
          group.headerAccount.id,
          direction,
        );
      } catch (err) {
        console.error("Error moving investment group:", err);
        this.$toast?.error?.("Investointikohteen siirto epäonnistui");
      }
    },
    openAccountTransactions(account) {
      this.selectedAccountForTx = account;
      this.showTransactionsDialog = true;
    },
    onTransactionsImported() {
      // Real-time listener automatically receives new transactions
    },
    async onBudgetCreated(year) {
      await this.fetchYears();
      this.selectedYear = year;
      this.initRealtimeSubscriptions();
    },
    async onVersionRestored() {
      await this.fetchYears();
      this.initRealtimeSubscriptions();
    },
    toggleGroup(groupId) {
      this.$set(this.collapsedGroups, groupId, !this.collapsedGroups[groupId]);
    },
    collapseAllInvestments() {
      const collapsed = {};
      this.investmentGroups.forEach((g) => {
        collapsed[g.id] = true;
      });
      this.collapsedGroups = collapsed;
    },
    expandAllInvestments() {
      this.collapsedGroups = {};
    },
    groupTitleMatches(group) {
      if (!this.accountSearch) return false;
      const q = this.accountSearch.trim().toLowerCase();
      return (
        group.code.toLowerCase().includes(q) ||
        group.title.toLowerCase().includes(q)
      );
    },
    getTitleLevel(acc) {
      const num = String(acc.accountNumber || "").trim();
      const name = String(acc.accountName || "").toLowerCase();
      if (/^XX\d+/i.test(num)) return 0;
      if (num === "200")
        return name.includes("vastattavaa") || name.includes("oma pääoma")
          ? 1
          : 2;
      if (num === "225" || num === "237") return 2;
      if (num === "300")
        return name.includes("tuloslaskelma") || name.includes("myyntituotot")
          ? 1
          : 2;
      if (num === "350") return 2;
      if (num === "900")
        return name.includes("rahoitustuotot") ||
          name.includes("varainhankinnan")
          ? 1
          : 2;
      if (num.startsWith("90") || num.startsWith("91") || num.startsWith("94"))
        return 2;
      if (num === "400") return name.includes("materiaalit") ? 1 : 2;
      if (num === "423" || num === "445") return 2;
      if (num === "500") return name.includes("henkilöstö") ? 1 : 2;
      if (num === "599" || num === "600" || num === "630") return 2;
      if (num === "700") return name.includes("muut kulut") ? 1 : 2;
      if (num.startsWith("7") || num.startsWith("8")) return 2;
      if (num === "X00") return 1;
      if (num === "XXX") return 2;
      return 3;
    },
    calculateTitleSums(accounts) {
      return accounts.map((acc, idx) => {
        if (!acc.isTitle && !acc.isHeader) {
          return acc;
        }

        const myLvl = this.getTitleLevel(acc);
        const myNum = String(acc.accountNumber || "").trim();
        let arvioSum = 0;
        let totSum = 0;
        let count = 0;

        for (let j = idx + 1; j < accounts.length; j++) {
          const nextAcc = accounts[j];
          if (nextAcc.type !== acc.type) break;

          // In investment section, stay within the same XX project
          if (acc.type === "investment") {
            const nextNum = String(nextAcc.accountNumber || "").trim();
            if (/^XX\d+/i.test(nextNum)) break;
          }

          if (nextAcc.isTitle || nextAcc.isHeader) {
            const nextLvl = this.getTitleLevel(nextAcc);
            if (nextLvl <= myLvl && nextAcc.accountNumber !== myNum) {
              break;
            }
          } else if (!nextAcc.isTotal) {
            arvioSum += nextAcc.arvio || 0;
            totSum += nextAcc.totalToteutuma || 0;
            count++;
          }
        }

        // If consecutive titles at the same level had count 0, look ahead to their immediate block
        if (count === 0) {
          for (let j = idx + 1; j < accounts.length; j++) {
            const nextAcc = accounts[j];
            if (nextAcc.type !== acc.type) break;
            if (
              acc.type === "investment" &&
              /^XX\d+/i.test(String(nextAcc.accountNumber || ""))
            )
              break;

            if (!nextAcc.isTitle && !nextAcc.isHeader && !nextAcc.isTotal) {
              for (let k = j; k < accounts.length; k++) {
                const kAcc = accounts[k];
                if (kAcc.type !== acc.type) break;
                if (kAcc.isTitle || kAcc.isHeader) {
                  if (this.getTitleLevel(kAcc) <= myLvl) break;
                } else if (!kAcc.isTotal) {
                  arvioSum += kAcc.arvio || 0;
                  totSum += kAcc.totalToteutuma || 0;
                  count++;
                }
              }
              break;
            }
          }
        }

        const arvio = Math.round(arvioSum * 100) / 100;
        const totalToteutuma = Math.round(totSum * 100) / 100;
        const erotus = Math.round((arvio - totalToteutuma) * 100) / 100;

        return {
          ...acc,
          arvio,
          totalToteutuma,
          erotus,
          childCount: count,
        };
      });
    },
    getRowClass(item) {
      if (item.isTotal) return "font-weight-bold grey lighten-4";
      if (!this.isAccountModifiable(item))
        return "blue-grey lighten-5 font-weight-medium";
      return "";
    },
    isAccountModifiable(item) {
      if (!item) return false;
      if (item.isTotal) return false;
      if (item.isTitle || item.isHeader) return false;
      if (item.isModifiable !== undefined) return item.isModifiable;
      if (
        item.accountNumber &&
        (/^\d{1,3}$/.test(String(item.accountNumber).trim()) ||
          item.accountNumber === "XXX" ||
          /^XX/i.test(String(item.accountNumber).trim()) ||
          item.accountNumber === "X00")
      ) {
        return false;
      }
      return true;
    },
    getUsageColor(percentage) {
      if (percentage > 100) return "red--text text--darken-2";
      if (percentage >= 80) return "orange--text text--darken-2";
      return "green--text text--darken-2";
    },
    getUsageProgressColor(percentage) {
      if (percentage > 100) return "red";
      if (percentage >= 80) return "orange";
      return "green";
    },
    formatCurrency(val) {
      if (val === null || val === undefined || isNaN(val)) return "0,00 €";
      return (
        Number(val).toLocaleString("fi-FI", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }) + " €"
      );
    },
    formatDiff(val) {
      if (val === null || val === undefined || isNaN(val)) return "0,00 €";
      const prefix = val <= 0 ? "+" : "";
      return prefix + this.formatCurrency(-val);
    },
    isExpenseWithinBudget(arvio, toteutuma) {
      const a = Number(arvio) || 0;
      const t = Number(toteutuma) || 0;
      if (a < 0) {
        if (t < a) return false;
        if (t > 0 && t > Math.abs(a)) return false;
        return true;
      }
      if (a === 0) {
        return t >= 0;
      }
      return t <= a;
    },
    getAccountErotusColor(item) {
      if (!item) return "";
      const arvio = Number(item.arvio) || 0;
      const toteutuma = Number(item.totalToteutuma) || 0;

      if (arvio === 0 && toteutuma === 0) {
        return "grey--text";
      }

      // If arvio is negative (typical for expenses: e.g. arvio = -3500, toteutuma = -644.83)
      if (arvio < 0) {
        // If spending has exceeded budget (toteutuma is more negative than arvio, e.g. -4000 vs -3500)
        if (toteutuma < arvio || (toteutuma > 0 && toteutuma > Math.abs(arvio))) {
          return "red--text font-weight-bold";
        }
        // If spending is within budget (toteuma on vähemmän kuin tämä arvio)
        return "green--text font-weight-bold";
      }

      // If arvio is 0 and toteutuma is negative (expense occurred without budget)
      if (arvio === 0 && toteutuma < 0) {
        return "red--text font-weight-bold";
      }

      // If it's an expense account with positive arvio
      if (item.type === "expense") {
        if (toteutuma > arvio) {
          return "red--text font-weight-bold";
        }
        return "green--text font-weight-bold";
      }

      // If it's an income account
      if (item.type === "income") {
        if (toteutuma >= arvio) {
          return "green--text font-weight-bold";
        }
        return "orange--text font-weight-bold";
      }

      return item.erotus >= 0
        ? "green--text font-weight-bold"
        : "red--text font-weight-bold";
    },
    exportToExcel() {
      if (!this.budgetData) return;

      const wb = XLSX.utils.book_new();

      // Overview sheet
      const overviewData = [
        ["NOORMARKUN VPK - TALOUSARVIO JA TOTEUTUMA", this.selectedYear],
        [],
        [
          "Kustannuspaikka",
          "Nimi",
          "Tuotot Arvio",
          "Tuotot Toteutuma",
          "Tuotot Erotus",
          "Kulut Arvio",
          "Kulut Toteutuma",
          "Kulut Erotus",
          "Tulos Arvio",
          "Tulos Toteutuma",
          "Kulutusaste %",
        ],
      ];

      this.overviewItems.forEach((it) => {
        overviewData.push([
          it.code,
          it.name,
          it.incomeArvio,
          it.incomeToteutuma,
          it.incomeErotus,
          it.expenseArvio,
          it.expenseToteutuma,
          it.expenseErotus,
          it.netArvio,
          it.netToteutuma,
          `${it.kayttoAste} %`,
        ]);
      });

      const wsOverview = XLSX.utils.aoa_to_sheet(overviewData);
      XLSX.utils.book_append_sheet(wb, wsOverview, "Yhteenveto");

      // Per project sheets
      this.budgetData.projects.forEach((p) => {
        const pData = [
          [p.fullName, `Budjetti ${this.selectedYear}`],
          [],
          [
            "Tilinro",
            "Kirjanpidon tili",
            "Tilin nimi",
            "Tyyppi",
            "Kategoria",
            "Arvio",
            "Toteutuma",
            "Jäljellä / Erotus",
          ],
        ];

        // Sort by type: balance first, then income, then expense, then investment/other
        const sortedAccs = [...(p.accounts || [])].sort((a, b) => {
          const rank = (t) =>
            t === "balance" || t === "equity"
              ? 1
              : t === "income"
              ? 2
              : t === "expense"
              ? 3
              : 4;
          return rank(a.type) - rank(b.type);
        });

        sortedAccs.forEach((a) => {
          const stats = this.txStatsByAccountId[a.id] || { count: 0, sum: 0 };
          const tot = (a.baseToteutuma || 0) + stats.sum;
          const erotus = (a.arvio || 0) - tot;
          const typeLabel =
            a.type === "balance" || a.type === "equity"
              ? "Tase"
              : a.type === "income"
              ? "Tuotto"
              : a.type === "expense"
              ? "Kulu"
              : a.type === "investment"
              ? "Investointi"
              : "Muu";

          pData.push([
            a.accountNumber || "",
            a.subAccount || "",
            a.accountName || "",
            typeLabel,
            a.category || "",
            a.arvio || 0,
            tot,
            erotus,
          ]);
        });

        const wsProj = XLSX.utils.aoa_to_sheet(pData);
        XLSX.utils.book_append_sheet(wb, wsProj, p.name.substring(0, 31));
      });

      XLSX.writeFile(wb, `Budjetti_${this.selectedYear}_raportti.xlsx`);
    },
  },
};
</script>

<style scoped>
.font-mono {
  font-family: monospace;
}
.border-left-green {
  border-left: 4px solid #2e7d32 !important;
}
.border-left-red {
  border-left: 4px solid #c62828 !important;
}
.border-left-primary {
  border-left: 4px solid #1976d2 !important;
}
.border-left-orange {
  border-left: 4px solid #e65100 !important;
}
</style>
